'use client';

import { useMemo, useState } from 'react';
import ActionButton from '~/components/ActionButton';
import Button from '~/components/Button';
import Dropdown from '~/components/Dropdown';
import InputLabel from '~/components/InputLabel';
import Tooltip from '~/components/Tooltip';
import { Checkbox } from '~/components/ui/checkbox';
import usePopover from '~/hooks/usePopover';
import { cn } from '~/lib/utils';
import { typography } from '~/components/Typography';
import { api, type RouterInputs } from '~/utils/api';
import { serverError } from '~/utils/serverError';

import { PlusIcon } from '@heroicons/react/24/outline';
import Multiselect from './Multiselect';

type AssignAttributeInput = RouterInputs['attribute']['assignAttribute'];

type AssignNewFieldButtonProps = AssignAttributeProps;

const AssignNewFieldButton = (props: AssignNewFieldButtonProps) => {
  const { data: allAttributes } = api.attribute.getAllAttributes.useQuery();
  const { mutateAsync: assignAttributes } = api.attribute.assignAttribute.useMutation();
  const { id, open, handleOpen, handleClose, anchorEl } = usePopover<HTMLButtonElement>();
  const [newAttr, setNewAttr] = useState<Partial<
    Pick<AssignAttributeInput, 'attributeId' | 'value'>
  > | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const utils = api.useUtils();

  const availableAttributes = useMemo(
    () =>
      allAttributes?.filter(
        (attribute) =>
          !props.attributeAssignments.find((assignment) => assignment.attributeId === attribute.id) &&
          attribute.name.toLowerCase() !== 'brand',
      ) ?? [],
    [allAttributes, props.attributeAssignments],
  );

  const resetAndClose = () => {
    handleClose();
    setNewAttr(null);
  };

  const handleAddField = async () => {
    try {
      if (!newAttr?.attributeId || !newAttr?.value?.length) return;
      setIsSaving(true);
      const assignment = {
        id: '',
        attributeId: newAttr.attributeId,
        value: newAttr.value,
      };
      props.onChange?.(assignment);
      if (props.id) {
        await assignAttributes({
          [props.type === 'task' ? 'taskId' : 'scannerJobId']: props.id,
          ...assignment,
        });
        if (props.type === 'task') {
          await utils.task.get.invalidate();
        } else {
          await utils.scanner.getAllScannerJobs.invalidate();
        }
      }
      resetAndClose();
    } catch (e) {
      serverError(e as Error);
    } finally {
      setIsSaving(false);
    }
  };

  const canAddField = !!newAttr?.attributeId && !!newAttr.value?.length;
  const selectedAttribute = allAttributes?.find(
    (attribute) => attribute.id === newAttr?.attributeId,
  );
  const valueOptions =
    selectedAttribute?.values.map((value) => ({ label: value, value })) ?? [];

  const toggleValue = (value: string, checked: boolean) => {
    setNewAttr((prev) => {
      if (!prev?.attributeId) return prev;
      const current = prev.value ?? [];
      return {
        ...prev,
        value: checked ? [...current, value] : current.filter((item) => item !== value),
      };
    });
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-foreground h-8 gap-1.5 px-2"
        icon={<PlusIcon className="size-3.5" strokeWidth={2} />}
        label="Add field"
        disabled={!availableAttributes.length}
        onClick={(event) => {
          setNewAttr(null);
          handleOpen(event);
        }}
      />
      <Dropdown
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={resetAndClose}
        disablePortal={props.disablePortal}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div className="w-72 space-y-4 p-4">
          <div className="space-y-3">
            <div className="space-y-2">
              <InputLabel label="Field" />
              <ul
                className="-mx-1 max-h-48 space-y-0.5 overflow-y-auto"
                role="listbox"
                aria-label="Field"
              >
                {availableAttributes.map((attribute) => {
                  const isSelected = newAttr?.attributeId === attribute.id;

                  return (
                    <li key={attribute.id} role="option" aria-selected={isSelected}>
                      <button
                        type="button"
                        onClick={() =>
                          setNewAttr({
                            attributeId: attribute.id,
                            value: [],
                          })
                        }
                        className={cn(
                          'flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                          isSelected
                            ? 'bg-accent text-foreground font-medium'
                            : 'text-foreground hover:bg-accent/60',
                        )}
                      >
                        {attribute.name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            {newAttr?.attributeId ? (
              <div className="border-border space-y-2 border-t pt-3">
                <InputLabel label="Values" />
                <div className="max-h-40 space-y-1 overflow-y-auto">
                  {valueOptions.map((option) => {
                    const checked = newAttr.value?.includes(option.value) ?? false;

                    return (
                      <label
                        key={option.value}
                        className="hover:bg-muted/30 flex cursor-pointer items-center gap-2 rounded-md px-1 py-1.5"
                      >
                        <Checkbox
                          className="size-4"
                          checked={checked}
                          onCheckedChange={(value) => toggleValue(option.value, value === true)}
                        />
                        <span className="text-foreground text-sm">{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
          <div className="border-border flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" size="sm" label="Cancel" onClick={resetAndClose} />
            <Button
              size="sm"
              label="Add field"
              onClick={handleAddField}
              isLoading={isSaving}
              disabled={!canAddField}
            />
          </div>
        </div>
      </Dropdown>
    </>
  );
};

type SavingItems = Record<string, Omit<AssignAttributeInput, 'id'>>;

type AssignAttributeProps = {
  id: string | null;
  onChange?: (
    assignment: AssignAttributeProps['attributeAssignments'][0] & { _deleted?: boolean },
  ) => void;
  type: 'task' | 'scannerJob';
  attributeAssignments: Array<{
    id: string;
    attributeId: string;
    value: string[];
  }>;
  disablePortal?: boolean;
  isReadOnly?: boolean;
  /** Drop outer border when nested in a drawer Details tab */
  embedded?: boolean;
};

export default function AssignAttribute(props: AssignAttributeProps) {
  const { data: allAttributes } = api.attribute.getAllAttributes.useQuery();
  const { mutateAsync: assignAttribute } = api.attribute.assignAttribute.useMutation();
  const { mutateAsync: deleteAttributeAssignment } =
    api.attribute.deleteAttributeAssignment.useMutation();

  const utils = api.useUtils();
  const [savingItems, setSavingItems] = useState<SavingItems>({});

  const onCancel = (id: string) => {
    setSavingItems((prev) => {
      const newSavingItems = { ...prev };
      delete newSavingItems[id];
      return newSavingItems;
    });
  };

  const handleSave = async (row: AssignAttributeProps['attributeAssignments'][0]) => {
    const update = savingItems[row.id];
    if (!update) return;
    props.onChange?.({
      id: row.id,
      attributeId: update.attributeId,
      value: update.value,
    });
    if (props.id) {
      await assignAttribute(update);
      if (props.type === 'task') {
        await utils.task.get.invalidate();
      } else {
        await utils.scanner.getAllScannerJobs.invalidate();
      }
    }
    onCancel(row.id);
  };

  const handleDelete = async (row: AssignAttributeProps['attributeAssignments'][0]) => {
    setSavingItems((prev) => {
      const { [row.id]: rowId, ...newPrev } = prev;
      return newPrev;
    });
    if (props.id) {
      await deleteAttributeAssignment({ id: row.id });
      if (props.type === 'task') {
        await utils.task.get.invalidate();
      } else {
        await utils.scanner.getAllScannerJobs.invalidate();
      }
    } else {
      props.onChange?.({
        id: row.id,
        attributeId: row.attributeId,
        value: row.value,
        _deleted: true,
      });
    }
  };

  const handleEdit = (row: AssignAttributeProps['attributeAssignments'][0]) => {
    setSavingItems((prev) => ({
      ...prev,
      [row.id]: {
        [props.type === 'task' ? 'taskId' : 'scannerJobId']: props.id,
        attributeId: row.attributeId,
        value: row.value,
      },
    }));
  };

  return (
    <section
      className={
        props.embedded ? 'space-y-2' : 'border-border/60 space-y-2 border-t pt-6'
      }
    >
      <div className="flex items-center justify-between gap-2">
        <Tooltip
          content="Choose attributes so the right policies and variables apply."
          popoverProps={{ disablePortal: props.disablePortal ?? true }}
        >
          <p className={typography.section}>Attributes</p>
        </Tooltip>
        {!props.isReadOnly ? <AssignNewFieldButton {...props} /> : null}
      </div>

      {props.attributeAssignments?.length ? (
        <div className="border-border bg-card overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="text-muted-foreground text-[13px]">
                  <th className="w-[40%] px-4 pb-2 pt-3 font-medium">Field</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Value</th>
                  {!props.isReadOnly ? <th className="w-12 px-2 pb-2 pt-3" /> : null}
                </tr>
              </thead>
              <tbody>
                {props.attributeAssignments.map((row) => {
                  const attribute = allAttributes?.find((item) => item.id === row.attributeId);

                  return (
                    <tr
                      key={row.id}
                      className="border-border/50 hover:bg-muted border-t transition-colors"
                    >
                      <td className="px-4 py-3.5 align-middle">
                        <span className="text-foreground text-sm font-medium">
                          {attribute?.name || ''}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 align-middle">
                        <Multiselect
                          selected={
                            savingItems[row.id] ? savingItems[row.id]?.value || [] : row.value || []
                          }
                          options={attribute?.values.map((value) => ({ label: value, value })) || []}
                          onChange={(value) =>
                            setSavingItems((prev) => ({
                              ...prev,
                              [row.id]: {
                                ...prev[row.id]!,
                                value,
                              },
                            }))
                          }
                          readOnly={!savingItems[row.id]}
                          popoverProps={{ disablePortal: props.disablePortal }}
                        />
                      </td>
                      {!props.isReadOnly ? (
                        <td className="px-2 py-3.5 align-middle">
                          <div className="flex justify-end">
                            <ActionButton
                              isEditing={!!savingItems[row.id]}
                              disablePortal={props.disablePortal}
                              onCancel={() => onCancel(row.id)}
                              onSave={() => handleSave(row)}
                              onEdit={() => handleEdit(row)}
                              onDelete={() => handleDelete(row)}
                            />
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </section>
  );
}
